import { selectClippingBox } from "@/store/clipping-box-selectors";
import { useAppStore } from "@/store/store-hooks";
import { PointCloudObject } from "@/object-cache";
import { encodePly } from "@/tools/point-sampling/ply";
import { sampleLodPoints } from "@/tools/point-sampling/sample-lod-points";
import { RegionBox } from "@/tools/smart-clipping/smart-clipping";
import { downloadFile } from "@/utils/download";
import { GUID } from "@faro-lotv/foundation";
import { PotreeTree } from "@faro-lotv/lotv";
import { selectProjectId, selectProjectName } from "@faro-lotv/project-source";
import { useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import { create } from "zustand";

/** Enough points to find floors and walls at a few centimeters, in a file of a few tens of MB */
const MAX_SAMPLE_POINTS = 3_000_000;
/** The whole point cloud spreads over a larger area, more points keep a similar density */
const MAX_WHOLE_SAMPLE_POINTS = 4_000_000;
/** Same margin around the box as the floor area estimation, so the sample matches what the app computes from */
const SAMPLED_REGION_SCALE = 1.25;

/** What a sample covers: the region box, or the whole point cloud as in the benchmark ground truth */
export type FloorSampleScope = "region" | "whole";

type FloorSampleCaptureState = {
  status: "idle" | "requested" | "capturing";
  scope: FloorSampleScope;
  sampledPoints: number;
  error?: string;
  request(scope: FloorSampleScope): void;
};

/** Development tool: captures the selected points to tune the floor segmentation and area estimation offline */
export const useFloorSampleCaptureStore = create<FloorSampleCaptureState>()(
  (set) => ({
    status: "idle",
    scope: "region",
    sampledPoints: 0,
    request: (scope) =>
      set({ status: "requested", scope, sampledPoints: 0, error: undefined }),
  }),
);

type UseCaptureFloorSampleProps = {
  /** The point cloud of the selected dataset */
  pointCloud: PointCloudObject;
  /** The scans left out of the selection */
  hiddenScans: GUID[];
  /** The default region box, to tune the smart clipping too */
  smartClipping?: RegionBox;
};

/** Downloads the selected scans in the region box or in the whole point cloud, plus the current view, as a PLY sample */
export function useCaptureFloorSample({
  pointCloud,
  hiddenScans,
  smartClipping,
}: UseCaptureFloorSampleProps): void {
  const store = useAppStore();
  const camera = useThree((state) => state.camera);
  const status = useFloorSampleCaptureStore((state) => state.status);
  const running = useRef<AbortController>(undefined);
  useEffect(
    () => () => {
      running.current?.abort();
      useFloorSampleCaptureStore.setState({ status: "idle" });
    },
    [],
  );

  useEffect(() => {
    if (status !== "requested") return;
    useFloorSampleCaptureStore.setState({ status: "capturing" });
    const abort = new AbortController();
    running.current = abort;

    async function capture(): Promise<void> {
      const { scope } = useFloorSampleCaptureStore.getState();
      const isWhole = scope === "whole";
      const maxPoints = isWhole ? MAX_WHOLE_SAMPLE_POINTS : MAX_SAMPLE_POINTS;
      const state = store.getState();
      const region = selectClippingBox(state);
      const view = {
        position: camera.position.toArray(),
        quaternion: camera.quaternion.toArray(),
      };
      const hiddenScanIndices = new Set(
        hiddenScans.flatMap((id) => pointCloud.scanIdsToIndex?.[id] ?? []),
      );
      const start = performance.now();
      const sample = await sampleLodPoints(pointCloud, {
        region:
          region && !isWhole
            ? {
                ...region,
                size: [
                  region.size[0] * SAMPLED_REGION_SCALE,
                  region.size[1] * SAMPLED_REGION_SCALE,
                  region.size[2] * SAMPLED_REGION_SCALE,
                ],
              }
            : undefined,
        maxPoints,
        hiddenScanIndices,
        keepScanIndices: true,
        signal: abort.signal,
        onProgress: (sampledPoints) =>
          useFloorSampleCaptureStore.setState({ sampledPoints }),
      });
      const durationMs = Math.round(performance.now() - start);

      const { sentScans } = state.ipxOrderMode;
      const capturedAt = new Date().toISOString();
      const projectName = selectProjectName(state);
      pointCloud.updateMatrixWorld();
      const meta = {
        format: "sphere-xg-floor-sample",
        version: 3,
        capturedAt,
        projectId: selectProjectId(state),
        projectName,
        pointCloudId: pointCloud.iElement.id,
        datasetName: pointCloud.iElement.name,
        units: "m",
        up: "y",
        // Positions in the file are relative to origin, in world coordinates
        origin: sample.origin.toArray(),
        scope,
        // The box the user had, the points cover it enlarged by SAMPLED_REGION_SCALE unless the scope is whole
        region,
        sampledRegionScale: SAMPLED_REGION_SCALE,
        view,
        smartClipping: smartClipping && {
          box: {
            min: smartClipping.box.min.toArray(),
            max: smartClipping.box.max.toArray(),
          },
          // The box is in the frame turned by yaw around the vertical axis
          yaw: smartClipping.yaw,
        },
        scans: sentScans.map(({ id, position }) => ({
          id,
          // The scan property of the points
          index: pointCloud.scanIdsToIndex?.[id],
          position,
        })),
        sampling: {
          maxPoints,
          durationMs,
          depth: sample.depth,
          levelEnds: sample.levelEnds,
          nodeCount: sample.nodeCount,
          pointCount: sample.positions.length / 3,
        },
        // Maps the tree-local positions of the LOD nodes to world coordinates
        pointCloudMatrixWorld: pointCloud.matrixWorld.toArray(),
        tree: pointCloud.tree instanceof PotreeTree && {
          points: pointCloud.tree.metadata.points,
          // The converter coordinates, the original file ones, are tree-local positions plus boundingBox.min
          offset: pointCloud.tree.metadata.offset,
          scale: pointCloud.tree.metadata.scale,
          boundingBox: pointCloud.tree.metadata.boundingBox,
          attributes: pointCloud.tree.metadata.attributes.map(
            ({ name, size }) => ({ name, size }),
          ),
          hierarchyStepSize: pointCloud.tree.metadata.hierarchy.stepSize,
          // Points per depth of the nodes whose hierarchy is loaded
          pointsPerDepth: pointCloud.tree.nodes.reduce<number[]>(
            (counts, { depth, numPoints }) => {
              counts[depth] = (counts[depth] ?? 0) + numPoints;
              return counts;
            },
            [],
          ),
        },
      };
      const url = URL.createObjectURL(
        encodePly(
          sample.positions,
          [`meta ${JSON.stringify(meta)}`],
          sample.scanIndices,
        ),
      );
      const fileName = (projectName ?? "project").replace(/[^\w.-]+/g, "_");
      downloadFile(url, `floor-sample-${fileName}-${scope}-${capturedAt}.ply`);
      URL.revokeObjectURL(url);
    }

    capture()
      .then(() => useFloorSampleCaptureStore.setState({ status: "idle" }))
      .catch((error: unknown) => {
        if (abort.signal.aborted) return;
        useFloorSampleCaptureStore.setState({
          status: "idle",
          error: error instanceof Error ? error.message : String(error),
        });
      });
  }, [camera, hiddenScans, pointCloud, smartClipping, status, store]);
}
